/**
 * Reptilian Agent - Game State (Headless, no GUI)
 * Idle clicker game state definition
 */

import type { DailyMission } from './data/missions.js';
import type { OperationRace } from './data/evolution.js';

export type { OperationRace } from './data/evolution.js';

// Faction IDs
export type FactionId = 'illuminati' | 'grey' | 'mib_resistance';

export interface FactionRelation {
  factionId: FactionId;
  favorability: number;
  tier: 1 | 2 | 3 | 4 | 5;
}

export interface ActiveFactionEvent {
  eventId: string;
  factionId: FactionId;        // faction requesting help
  targetFactionId: FactionId;  // faction that loses favor
  expiresAt: number;
}

export interface FactionState {
  relations: Record<FactionId, FactionRelation>;
  currentEvent: ActiveFactionEvent | null;
  nextEventAt: number;
  totalEventsHelped: number;
  totalEventsIgnored: number;
}

// Agent rarity
export type AgentRarity = 'common' | 'rare' | 'epic' | 'legendary';

// Collected agent card
export interface AgentCard {
  agentId: string;
  obtainedAt: number;
  count: number;
}

// Agent collection state
export interface AgentState {
  collected: Record<string, AgentCard>;
  equipped: (string | null)[];
  totalCollected: number;
}

// Boss type IDs
export type BossId =
  | 'resistance_captain'
  | 'cia_agent'
  | 'mib_investigator'
  | 'un_inspector'
  | 'whistleblower'
  | 'journalist'
  | 'truth_seeker'
  | 'galactic_guardian';

// Active boss event
export interface BossEvent {
  bossId: BossId;
  spawnedAt: number;
  expiresAt: number;
  maxHp: number;
  currentHp: number;
  phase: 1 | 2;
  rewardClaimed: boolean;
  doubleDamageUntil: number; // ad bonus: 2x damage until this timestamp
  reviveUsed: boolean;       // ad bonus: revive used flag
}

// Boss persistent state
export interface BossState {
  current: BossEvent | null;
  nextBossAt: number;
  totalBossesDefeated: number;
  totalBossesExpired: number;
  bossKillStreak: number;
}

// Producer (generator) ownership data
export interface ProducerOwned {
  count: number;
}

// Active boost effect
export interface ActiveBoost {
  type: 'cps2x' | 'tap3x' | 'tap5x' | 'cps3x' | 'tap10x' | 'cps5x' | 'autoClick';
  multiplier: number;
  endsAt: number; // timestamp
}

// Golden event on screen
export interface GoldenEvent {
  id: string;
  type: 'intel_leak' | 'agent_frenzy' | 'money_launder' | 'facility_boost' | 'supply_drop';
  appearedAt: number;
  expiresAt: number;
}

// Achievement status
export interface AchievementStatus {
  unlocked: boolean;
  unlockedAt?: number;
}

// Prestige 1 upgrade levels
export type PrestigeUpgradeId =
  | 'galaxyMultiplier'
  | 'infiltrationAccel'
  | 'tapEnhance'
  | 'offlineExpand'
  | 'offlineEfficiency'
  | 'agentDiscount'
  | 'autoClick'
  | 'goldenLuck'
  | 'comboKeep'
  | 'quickStart'
  | 'infiltrationMastery'
  | 'agentNetwork'
  | 'quickInfiltration'
  | 'criticalChance'
  | 'criticalDamage';

// Prestige 2 upgrade IDs
export type Prestige2UpgradeId =
  | 'galaxySpeed'
  | 'autoPrestige'
  | 'permanentAgents'
  | 'dimensionAccel'
  | 'galaxyMemory'
  | 'spacetimeWarp'
  | 'galaxyAutomate';

// Conquest state per country
export interface ConquestCountryState {
  invested: number;
  conquered: boolean;
  facilities: [number, number, number];
}

export interface ConquestState {
  activeCountry: string | null; // currently infiltrating country
  countries: Record<string, ConquestCountryState>;
  endingUnlocked: boolean;
}

// === Infiltration Run (active choose-your-path mini-game) ===

/** Per-agent stats used only by infiltration choices. */
export interface InfiltrationStats {
  disguise: number;   // stealth / identity swaps
  combat: number;     // force / takedown choices
  hacking: number;    // systems / electronics choices
  charisma: number;   // persuasion / deception choices
}

/** Result of the most recent choice, for the reveal animation. */
export interface InfilChoiceResult {
  choiceId: string;
  success: boolean;
  roll: number;
}

/** State of one in-progress (or just-finished) infiltration run. */
export interface InfiltrationRunState {
  active: boolean;
  agentStats: InfiltrationStats;   // stats snapshot for this run
  eventQueue: string[];            // ordered event ids (5-6)
  currentIndex: number;
  detection: number;               // 0..100
  ambition: number;                // accumulated risk from successful choices
  successfulChoices: number;
  accumulatedIp: number;
  accumulatedDocs: number;
  lastChoiceResult: InfilChoiceResult | null;
  finished: boolean;
  finalOutcome: 'success' | 'fail' | null;
  earnedCareer: string | null;     // career tier id on success
  rewardIp: number;                // IP actually granted at resolve
  rewardDocs: number;              // docs actually granted at resolve
  retryUsed: boolean;              // ad retry consumed (once per run)
  tutorial: boolean;               // this run is the scripted tutorial
}

/** Persistent infiltration progression. */
export interface InfiltrationState {
  run: InfiltrationRunState | null;
  baseStats: InfiltrationStats;    // deployed agent base stats (grows on success)
  tutorialDone: boolean;
  totalRuns: number;
  totalSuccess: number;
  highestCareer: string | null;
  cooldownUntil: number;
}

// === Operation Mode (Plague-style world domination) — separate from the idle game ===

export type OperationDifficulty = 'easy' | 'normal' | 'brutal' | 'impossible';

/** A collectable DNA burst that periodically appears over the world map. */
export interface DnaBubble {
  regionId: string;
  amount: number;
  spawnedTick: number;
}

/** State of one Operation ("World Domination") run. Fully self-contained. */
export interface OperationState {
  active: boolean;                    // a run is in progress or awaiting report
  race: OperationRace | null;         // chosen reptilian archetype
  difficulty: OperationDifficulty;
  infection: Record<string, number>;  // regionId -> infiltration 0..100
  resistance: number;                 // human resistance gauge 0..100 (100 = loss)
  dna: number;                        // spendable infiltration DNA
  boughtNodes: string[];              // purchased evolution node ids
  elapsedTicks: number;
  outcome: 'win' | 'lose' | null;     // set when the run resolves
  startRegion: string | null;         // where the operation began
  bubble: DnaBubble | null;           // current collectable DNA bubble
}

export interface GameState {
  // Primary currency
  ip: number; // Infiltration Points (current)
  totalIpEarned: number; // total IP ever earned (for prestige calc)
  totalIpThisRun: number; // total IP earned in current run

  // Secondary currencies
  gc: number; // Galactic Credits
  totalGcEarned: number;
  cores: number; // Galactic Cores (prestige 2)
  totalCoresEarned: number;
  classifiedDocs: number; // bonus currency
  totalDocsUsed: number;

  // Tap
  tapCount: number;
  baseTapPower: number; // base IP per tap
  comboCount: number;
  comboLastTapAt: number; // timestamp
  maxCombo: number;

  // Producers (12 tiers)
  producers: Record<string, ProducerOwned>;

  // Click upgrades purchased (indices)
  clickUpgradesBought: number[];

  // Special upgrades purchased (IDs)
  specialUpgradesBought: string[];

  // Prestige
  prestigeCount: number; // total prestige 1 count
  prestigeUpgrades: Record<PrestigeUpgradeId, number>; // upgrade levels

  // Prestige 2
  prestige2Count: number;
  prestige2Upgrades: Record<Prestige2UpgradeId, number>;

  // Achievements
  achievements: Record<string, AchievementStatus>;

  // Active boosts
  activeBoosts: ActiveBoost[];

  // Prestige boost (galactic amplifier) — purchased but not yet consumed
  prestigeBoostActive: boolean;

  // Golden events
  currentGoldenEvent: GoldenEvent | null;
  goldenEventsTouched: number;
  lastGoldenEventAt: number;
  nextGoldenEventAt: number;

  // Offline
  lastOnlineAt: number; // timestamp
  offlineIpPending: number; // pending offline IP to show in popup

  // Stats
  totalPlayTimeMs: number;
  sessionStartAt: number;
  totalAdsWatched: number;
  gameStartedAt: number;

  // Ad cooldowns
  adCooldowns: Record<string, number>; // adType -> available timestamp

  // Daily missions
  dailyMissions: DailyMission[];
  dailyMissionDate: string; // YYYY-MM-DD UTC
  dailyMissionAllClaimed: boolean;

  // Rank (promoted grade, not auto-calculated)
  rank: string; // 'E' | 'D' | 'C' | 'B' | 'A' | 'S' | 'SS' | 'SSS'

  // Conquest
  conquest: ConquestState;

  // Infiltration run (active choose-your-path mini-game)
  infiltration: InfiltrationState;

  // Operation mode (Plague-style world domination — separate from the idle game)
  operation: OperationState;

  // Boss
  boss: BossState;

  // Agent collection (persists across prestiges)
  agentCollection: AgentState;

  // Faction system (persists across prestiges)
  faction: FactionState;

  // Settings (saved but not game logic)
  bgmVolume: number;
  sfxVolume: number;
}

export function createInitialState(): GameState {
  const now = Date.now();
  return {
    ip: 0,
    totalIpEarned: 0,
    totalIpThisRun: 0,

    gc: 0,
    totalGcEarned: 0,
    cores: 0,
    totalCoresEarned: 0,
    classifiedDocs: 0,
    totalDocsUsed: 0,

    tapCount: 0,
    baseTapPower: 1,
    comboCount: 0,
    comboLastTapAt: 0,
    maxCombo: 0,

    producers: {},

    clickUpgradesBought: [],
    specialUpgradesBought: [],

    prestigeCount: 0,
    prestigeUpgrades: {
      galaxyMultiplier: 0,
      infiltrationAccel: 0,
      tapEnhance: 0,
      offlineExpand: 0,
      offlineEfficiency: 0,
      agentDiscount: 0,
      autoClick: 0,
      goldenLuck: 0,
      comboKeep: 0,
      quickStart: 0,
      infiltrationMastery: 0,
      agentNetwork: 0,
      quickInfiltration: 0,
      criticalChance: 0,
      criticalDamage: 0,
    },

    prestige2Count: 0,
    prestige2Upgrades: {
      galaxySpeed: 0,
      autoPrestige: 0,
      permanentAgents: 0,
      dimensionAccel: 0,
      galaxyMemory: 0,
      spacetimeWarp: 0,
      galaxyAutomate: 0,
    },

    achievements: {},
    activeBoosts: [],
    prestigeBoostActive: false,

    currentGoldenEvent: null,
    goldenEventsTouched: 0,
    lastGoldenEventAt: now,
    nextGoldenEventAt: now + 300_000 + Math.random() * 600_000,

    lastOnlineAt: now,
    offlineIpPending: 0,

    totalPlayTimeMs: 0,
    sessionStartAt: now,
    totalAdsWatched: 0,
    gameStartedAt: now,

    adCooldowns: {},

    dailyMissions: [],
    dailyMissionDate: '',
    dailyMissionAllClaimed: false,

    rank: 'E',

    conquest: {
      activeCountry: null,
      countries: {},
      endingUnlocked: false,
    },

    infiltration: {
      run: null,
      baseStats: { disguise: 10, combat: 10, hacking: 10, charisma: 10 },
      tutorialDone: false,
      totalRuns: 0,
      totalSuccess: 0,
      highestCareer: null,
      cooldownUntil: 0,
    },

    operation: {
      active: false,
      race: null,
      difficulty: 'normal',
      infection: {},
      resistance: 0,
      dna: 0,
      boughtNodes: [],
      elapsedTicks: 0,
      outcome: null,
      startRegion: null,
      bubble: null,
    },

    boss: {
      current: null,
      nextBossAt: now + 10 * 60_000 + Math.random() * 10 * 60_000,
      totalBossesDefeated: 0,
      totalBossesExpired: 0,
      bossKillStreak: 0,
    },

    agentCollection: {
      collected: {},
      equipped: [null, null, null],
      totalCollected: 0,
    },

    faction: {
      relations: {
        illuminati: { factionId: 'illuminati', favorability: 0, tier: 1 },
        grey: { factionId: 'grey', favorability: 0, tier: 1 },
        mib_resistance: { factionId: 'mib_resistance', favorability: 0, tier: 1 },
      },
      currentEvent: null,
      nextEventAt: now + 180_000,
      totalEventsHelped: 0,
      totalEventsIgnored: 0,
    },

    bgmVolume: 50,
    sfxVolume: 70,
  };
}
